import { useEffect, useState } from "react";
import { businessesApi, integrationsApi } from "@/lib/api";
import { useAuth, isMaster } from "@/lib/auth";
import { toast } from "sonner";
import { Building2, Plus, Trash2, RefreshCcw, Loader2, Save, Edit2, X } from "lucide-react";

const EMPTY = {
  name: "", code: "",
  xero_tracking_category_id: "", xero_tracking_category_name: "",
  xero_tracking_option_id: "", xero_tracking_option_name: "",
  zoho_cost_center_id: "", zoho_cost_center_name: "", zoho_tag_id: "",
};

export default function Branches() {
  const { user } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [bizId, setBizId] = useState("");
  const [branches, setBranches] = useState([]);
  const [categories, setCategories] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const biz = businesses.find((b) => b.id === bizId);

  useEffect(() => {
    businessesApi.list().then((b) => {
      setBusinesses(b);
      setBizId(isMaster(user) ? (b[0]?.id || "") : (user?.business_ids?.[0] || ""));
    });
  }, [user]);

  const loadBranches = () => { if (bizId) businessesApi.branches(bizId).then(setBranches); };
  useEffect(() => { loadBranches(); setCategories([]); setEditing(null); setForm(EMPTY); }, [bizId]);

  const fetchTracking = async () => {
    setFetching(true);
    try {
      const res = await integrationsApi.trackingCategories(bizId);
      setCategories(res.categories || []);
      toast.success(`Loaded ${res.categories.length} ${biz?.accounting === "xero" ? "tracking categories" : "reporting tags"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to fetch");
    } finally { setFetching(false); }
  };

  const startEdit = (b) => { setEditing(b.id); setForm({ ...EMPTY, ...b }); };
  const startNew = () => { setEditing("new"); setForm(EMPTY); };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") {
        await businessesApi.addBranch(bizId, form);
        toast.success("Branch added");
      } else {
        await businessesApi.updateBranch(bizId, editing, form);
        toast.success("Branch updated");
      }
      cancel();
      loadBranches();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this branch?")) return;
    await businessesApi.deleteBranch(bizId, id);
    loadBranches();
  };

  const onXeroCategory = (id) => {
    const cat = categories.find((c) => c.id === id);
    setForm((f) => ({
      ...f,
      xero_tracking_category_id: id,
      xero_tracking_category_name: cat?.name || "",
      xero_tracking_option_id: "",
      xero_tracking_option_name: "",
    }));
  };

  const onXeroOption = (id) => {
    const cat = categories.find((c) => c.id === form.xero_tracking_category_id);
    const opt = cat?.options?.find((o) => o.id === id);
    setForm((f) => ({ ...f, xero_tracking_option_id: id, xero_tracking_option_name: opt?.name || "" }));
  };

  const onZohoTag = (id) => {
    const tag = categories.find((c) => c.id === id);
    setForm((f) => ({ ...f, zoho_tag_id: id, zoho_cost_center_id: "", zoho_cost_center_name: "" }));
  };

  const onZohoOption = (id) => {
    const tag = categories.find((c) => c.id === form.zoho_tag_id);
    const opt = tag?.options?.find((o) => o.id === id);
    setForm((f) => ({ ...f, zoho_cost_center_id: id, zoho_cost_center_name: opt?.name || "" }));
  };

  const selectedXeroCat = categories.find((c) => c.id === form.xero_tracking_category_id);
  const selectedZohoTag = categories.find((c) => c.id === form.zoho_tag_id);

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-4xl font-bold text-espresso" data-testid="branches-title">Branches</h1>
          <p className="text-espresso-ink/60 mt-2 max-w-2xl">Each branch maps to a Xero <b>Tracking Option</b> or a Zoho <b>reporting tag option</b>. When someone uploads a document tagged to a branch, that option is attached to every line on the published bill.</p>
        </div>
        <select data-testid="branches-biz" value={bizId} onChange={(e) => setBizId(e.target.value)} className="px-3 py-2 bg-white border border-canvas-border rounded-lg text-sm brand-focus">
          {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>

      <div className="bg-white border border-canvas-border rounded-2xl p-6 shadow-soft flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="font-display text-lg font-bold text-espresso">{biz?.name} — {biz?.accounting === "xero" ? "Xero Tracking Options" : "Zoho Reporting Tags"}</div>
          <div className="text-sm text-espresso-ink/60 mt-1">
            {biz?.connected ? `${categories.length} loaded` : "Connect accounting first on Integrations page"}
          </div>
          {biz?.connected && branches.length === 0 && (
            <div className="text-xs text-status-duplicate mt-2 font-semibold" data-testid="no-branches-warning">
              ⚠ No branches configured — every published bill will land WITHOUT a branch tag. Add at least one branch below.
            </div>
          )}
        </div>
        <div className="flex gap-3">
          <button onClick={fetchTracking} disabled={fetching || !biz?.connected} data-testid="fetch-tracking" className="inline-flex items-center gap-2 px-4 py-2 border border-canvas-border rounded-lg text-sm font-semibold text-espresso hover:bg-canvas-border/50 disabled:opacity-50">
            {fetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
            {biz?.accounting === "xero" ? "Pull Xero Options" : "Pull Zoho Tags"}
          </button>
          <button onClick={startNew} data-testid="add-branch" className="inline-flex items-center gap-2 px-4 py-2 bg-espresso text-canvas rounded-lg text-sm font-semibold hover:bg-espresso-light">
            <Plus className="w-4 h-4" /> Add branch
          </button>
        </div>
      </div>

      {editing && (
        <form onSubmit={save} className="bg-white border-2 border-gold rounded-2xl p-6 shadow-soft space-y-5" data-testid="branch-form">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-bold text-espresso">{editing === "new" ? "New branch" : "Edit branch"}</h2>
            <button type="button" onClick={cancel} className="text-espresso-ink/50 hover:text-espresso"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">Branch name</label>
              <input data-testid="branch-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full mt-1.5 px-3 py-2.5 border border-canvas-border rounded-lg brand-focus" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">Internal code</label>
              <input data-testid="branch-code" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="w-full mt-1.5 px-3 py-2.5 border border-canvas-border rounded-lg brand-focus" />
            </div>
          </div>

          {biz?.accounting === "xero" ? (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">Xero Tracking Category</label>
                <select value={form.xero_tracking_category_id || ""} onChange={(e) => onXeroCategory(e.target.value)} className="w-full mt-1.5 px-3 py-2.5 border border-canvas-border rounded-lg brand-focus" data-testid="branch-xero-cat">
                  <option value="">— select —</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">Xero Option (Branch value)</label>
                <select value={form.xero_tracking_option_id || ""} onChange={(e) => onXeroOption(e.target.value)} disabled={!selectedXeroCat} className="w-full mt-1.5 px-3 py-2.5 border border-canvas-border rounded-lg brand-focus disabled:bg-canvas-border/30" data-testid="branch-xero-opt">
                  <option value="">— select —</option>
                  {selectedXeroCat?.options?.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">Zoho Reporting Tag</label>
                <select value={form.zoho_tag_id || ""} onChange={(e) => onZohoTag(e.target.value)} className="w-full mt-1.5 px-3 py-2.5 border border-canvas-border rounded-lg brand-focus" data-testid="branch-zoho-tag">
                  <option value="">— select —</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-espresso-ink/60 font-semibold">Zoho Option (Branch value)</label>
                <select value={form.zoho_cost_center_id || ""} onChange={(e) => onZohoOption(e.target.value)} disabled={!selectedZohoTag} className="w-full mt-1.5 px-3 py-2.5 border border-canvas-border rounded-lg brand-focus disabled:bg-canvas-border/30" data-testid="branch-zoho-opt">
                  <option value="">— select —</option>
                  {selectedZohoTag?.options?.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
            </div>
          )}

          {categories.length === 0 && (
            <div className="text-xs text-espresso-ink/60 bg-canvas rounded-lg p-3">Tip: click &quot;Pull Xero Options / Zoho Tags&quot; first to populate the dropdowns from your connected accounting system.</div>
          )}

          <div className="flex justify-end gap-3">
            <button type="button" onClick={cancel} className="px-4 py-2 border border-canvas-border rounded-lg text-sm font-semibold text-espresso">Cancel</button>
            <button type="submit" data-testid="branch-save" className="inline-flex items-center gap-2 px-4 py-2 bg-espresso text-canvas rounded-lg text-sm font-semibold hover:bg-espresso-light">
              <Save className="w-4 h-4" /> Save
            </button>
          </div>
        </form>
      )}

      <div className="bg-white border border-canvas-border rounded-2xl overflow-hidden shadow-soft">
        {branches.length === 0 ? (
          <div className="p-14 text-center text-espresso-ink/50">No branches yet. Add one to start tagging documents.</div>
        ) : (
          <table className="w-full">
            <thead className="bg-canvas text-xs uppercase tracking-wider text-espresso-ink/60">
              <tr>
                <th className="text-left px-6 py-3">Branch</th>
                <th className="text-left px-6 py-3">Code</th>
                <th className="text-left px-6 py-3">{biz?.accounting === "xero" ? "Tracking Option" : "Reporting Tag / Option"}</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {branches.map((b) => (
                <tr key={b.id} className={`border-t border-canvas-border ${b.hidden ? "bg-canvas/60 text-espresso-ink/50" : ""}`} data-testid={`branch-row-${b.id}`}>
                  <td className="px-6 py-3 font-medium text-espresso">
                    <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-gold" /> {b.name}</div>
                  </td>
                  <td className="px-6 py-3 text-sm text-espresso-ink">{b.code}</td>
                  <td className="px-6 py-3 text-sm">
                    {biz?.accounting === "xero" ? (
                      b.xero_tracking_option_name ? (
                        <span className="text-espresso"><b>{b.xero_tracking_category_name}</b> → {b.xero_tracking_option_name}</span>
                      ) : <span className="text-espresso-ink/40">Not mapped</span>
                    ) : (
                      b.zoho_cost_center_name ? (
                        <span className="text-espresso">Tag → <b>{b.zoho_cost_center_name}</b></span>
                      ) : <span className="text-espresso-ink/40">Not mapped</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right space-x-2">
                    {isMaster(user) && (
                      <button
                        data-testid={`branch-toggle-hidden-${b.id}`}
                        onClick={async () => {
                          try {
                            await businessesApi.setBranchHidden(bizId, b.id, !b.hidden);
                            toast.success(b.hidden ? "Now visible to users" : "Now hidden from users");
                            loadBranches();
                          } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                        }}
                        className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold transition-colors ${b.hidden ? "bg-status-duplicate/10 text-status-duplicate border border-status-duplicate/40 hover:bg-status-duplicate/20" : "bg-status-synced/10 text-status-synced border border-status-synced/40 hover:bg-status-synced/20"}`}
                      >
                        {b.hidden ? "Hidden" : "Visible"}
                      </button>
                    )}
                    <button onClick={() => startEdit(b)} data-testid={`edit-branch-${b.id}`} className="text-espresso-ink hover:text-espresso"><Edit2 className="w-4 h-4 inline" /></button>
                    <button onClick={() => del(b.id)} className="text-status-duplicate hover:opacity-70"><Trash2 className="w-4 h-4 inline" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
